// src/scenes/GestionStock/TransfertStockModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { inventoryService } from '../../services/inventory';

const TransfertStockModal = ({ open, onClose, stock, succursales, onSuccess }) => {
  const [succursaleDestination, setSuccursaleDestination] = useState('');
  const [quantite, setQuantite] = useState('');
  const [motif, setMotif] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!succursaleDestination) {
      setError('Veuillez sélectionner une succursale de destination');
      return;
    }

    if (!quantite) {
      setError('Veuillez saisir une quantité');
      return;
    }

    const quantiteValue = parseFloat(quantite);
    if (isNaN(quantiteValue) || quantiteValue <= 0) {
      setError('Veuillez saisir une quantité valide');
      return;
    }

    if (quantiteValue > stock.stock_actuel) {
      setError('La quantité à transférer dépasse le stock actuel');
      return;
    }

    if (!motif.trim()) {
      setError('Veuillez saisir un motif');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await inventoryService.transfertStock({
        produit_id: stock.produit,
        succursale_origine: stock.succursale,
        succursale_destination: parseInt(succursaleDestination),
        quantite: quantiteValue,
        motif: motif.trim()
      });

      onSuccess();
      handleClose();
    } catch (err) {
      console.error('Erreur transfert:', err);
      setError(err.response?.data?.error || 'Erreur lors du transfert de stock');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccursaleDestination('');
    setQuantite('');
    setMotif('');
    setError('');
    onClose();
  };

  if (!stock) return null;

  // Filtrer pour ne pas montrer la succursale d'origine
  const succursalesDestination = succursales.filter(
    s => s.id !== stock.succursale
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Transférer le stock
        <Typography variant="subtitle1" fontWeight="bold">
          {stock.produit_nom}
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary">
          De: {stock.succursale_nom} - Stock actuel: {stock.stock_actuel}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Succursale de destination</InputLabel>
            <Select
              value={succursaleDestination}
              label="Succursale de destination"
              onChange={(e) => setSuccursaleDestination(e.target.value)}
            >
              {succursalesDestination.map(succ => (
                <MenuItem key={succ.id} value={succ.id}>{succ.nom}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Quantité à transférer"
            type="number"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            inputProps={{ 
              step: "0.01",
              min: 0.01,
              max: stock.stock_actuel
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Motif"
            multiline
            rows={2}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Ex: Réapprovisionnement, Transfert entre succursales..."
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="info"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Transférer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransfertStockModal;